import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircleIcon } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { z } from "zod";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import Logo from "../../components/Logo";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { useResetPasswordAcceptMutation } from "../../services/api";

function inferErrorKey(error: unknown): string {
  const e = error as { data?: { error?: string; message?: string } };
  const code = e?.data?.error;
  if (code === "EXPIRED_TOKEN") return "reset-password-accept.error-expired";
  if (code === "TOKEN_ALREADY_USED") return "reset-password-accept.error-already-used";
  if (code === "INVALID_TOKEN") return "reset-password-accept.error-invalid";
  if (e?.data?.message) return e.data.message;
  return "reset-password-accept.error-default";
}

export default function ResetPasswordAccept() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();
  const [resetPasswordAccept] = useResetPasswordAcceptMutation();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !email) {
      setError(t("reset-password-accept.missing-params"));
    }
  }, [token, email, t]);

  const schema = z
    .object({
      email: z
        .string()
        .min(1, t("reset-password-accept.required-email"))
        .email(t("reset-password-accept.invalid-email")),
      password: z
        .string()
        .min(10, t("reset-password-accept.password-too-short"))
        .regex(/[A-Z]/, t("reset-password-accept.password-missing-uppercase"))
        .regex(/[a-z]/, t("reset-password-accept.password-missing-lowercase"))
        .regex(/[0-9]/, t("reset-password-accept.password-missing-number"))
        .regex(
          /[^A-Za-z0-9]/,
          t("reset-password-accept.password-missing-symbol")
        ),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: t("reset-password-accept.passwords-must-match"),
    });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: email ?? "",
      password: "",
      confirmPassword: "",
    },
  });

  // Re-seed the email field when the URL param arrives after first render.
  useEffect(() => {
    if (email) form.setValue("email", email);
  }, [email, form]);

  async function onSubmit(values: Values) {
    if (!token) {
      setError(t("reset-password-accept.missing-token"));
      return;
    }
    setError(null);
    try {
      const result = await resetPasswordAccept({
        token,
        email: values.email,
        password: values.password,
      }).unwrap();
      const has2FA = Boolean((result as { has2FA?: boolean })?.has2FA);
      showNotification("success", t("reset-password-accept.success"));
      navigate(has2FA ? "/dashboard/app" : "/setup-2fa?from=password-reset");
    } catch (err) {
      setError(t(inferErrorKey(err)));
    }
  }

  const submitDisabled =
    !token || form.formState.isSubmitting;

  return (
    <>
      <Helmet>
        <title>{t("reset-password-accept.title")} | Betterfield</title>
      </Helmet>
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <Form {...form}>
          <form
            autoComplete="off"
            noValidate
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <div className="space-y-2">
              <h1 className="text-2xl font-bold tracking-tight">
                {t("reset-password-accept.title")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("reset-password-accept.subtitle")}
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircleIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("reset-password-accept.email-label")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="username"
                      disabled={!!email}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("reset-password-accept.password-label")}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="new-password"
                      show={showPassword}
                      onToggleShow={() => setShowPassword((s) => !s)}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t("reset-password-accept.password-requirements")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("reset-password-accept.confirm-password-label")}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="new-password"
                      show={showConfirmPassword}
                      onToggleShow={() =>
                        setShowConfirmPassword((s) => !s)
                      }
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col items-center gap-3">
              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={submitDisabled}
              >
                {t("reset-password-accept.submit-button")}
              </Button>
              <Button asChild variant="link" size="sm">
                <RouterLink to="/forgot-password">
                  {t("reset-password-accept.link-expired")}
                </RouterLink>
              </Button>
              <Button asChild variant="link" size="sm">
                <RouterLink to="/login">
                  {t("reset-password-accept.back-to-login")}
                </RouterLink>
              </Button>
            </div>
          </form>
        </Form>
      </main>
    </>
  );
}
